import { getDebateTarget, isDebatePast } from '../utils/debateTime';
// API configuration
const API_BASE = process.env.REACT_APP_API_BASE || (
    process.env.NODE_ENV === 'production'
        ? 'https://api.ukpetitiontracker.com/api'
        : 'http://localhost:3002/api'
);

// Local cache for frontend (lightweight, short-term)
const cache = new Map();
const CACHE_DURATION        = 5  * 60 * 1000; // 5 min  — live data (open petitions)
const STATIC_CACHE_DURATION = 30 * 60 * 1000; // 30 min — closed/rejected (rarely change)

// Cache keys for static (non-live) data
const STATIC_CACHE_KEYS = ['closed', 'rejected'];
const isStaticKey = (key) => STATIC_CACHE_KEYS.some(k => key.includes(k));

// Extract petition ID from URL
const extractPetitionId = (url) => {
    const match = url.match(/\/petitions\/(\d+)/);
    return match ? match[1] : null;
};

// Check if cached data is still valid
const isCacheValid = (cacheKey, timestamp) => {
    const ttl = isStaticKey(cacheKey) ? STATIC_CACHE_DURATION : CACHE_DURATION;
    return Date.now() - timestamp < ttl;
};

// Get cached data if valid
const getCachedData = (cacheKey) => {
    const cached = cache.get(cacheKey);
    if (cached && isCacheValid(cacheKey, cached.timestamp)) {
        return cached.data;
    }
    return null;
};

// Set cache data
const setCacheData = (cacheKey, data) => {
    cache.set(cacheKey, {
        data,
        timestamp: Date.now()
    });
};

// Process raw petition data (same as before but simplified)
const processPetitionData = (rawData) => {
    const petition = rawData.data.attributes;

    // Process country data (exclude UK for better visualization)
    const countries = petition.signatures_by_country || [];
    const ukData = countries.find(c => c.code === 'GB');
    const internationalData = countries.filter(c => c.code !== 'GB');
    const sortedInternational = internationalData.sort((a, b) => b.signature_count - a.signature_count);

    // Group smaller countries as "Others"
    const top8Countries = sortedInternational.slice(0, 20);
    const otherCountries = sortedInternational.slice(20);
    const othersCount = otherCountries.reduce((sum, c) => sum + c.signature_count, 0);

    const processedCountries = [...top8Countries];
    if (othersCount > 0) {
        processedCountries.push({ name: 'Others', signature_count: othersCount, code: 'XX' });
    }

    // Process constituency data (top 15)
    const constituencies = petition.signatures_by_constituency || [];
    const topConstituencies = constituencies
        .sort((a, b) => b.signature_count - a.signature_count)
        .slice(0, -1);

    // Process region data
    const regions = petition.signatures_by_region || [];
    const sortedRegions = regions.sort((a, b) => b.signature_count - a.signature_count);

    // Process government response
    const governmentResponse = petition.government_response ? {
        summary: petition.government_response.summary || '',
        details: petition.government_response.details || '',
        respondedOn: petition.government_response.responded_on || petition.government_response_at,
        createdAt: petition.government_response.created_at,
        updatedAt: petition.government_response.updated_at
    } : null;

    // Process debate information
    const debate = petition.debate ? {
        debatedOn: petition.debate.debated_on || petition.scheduled_debate_date,
        transcriptUrl: petition.debate.transcript_url || '',
        videoUrl: petition.debate.video_url || '',
        debatePackUrl: petition.debate.debate_pack_url || '',
        publicEngagementUrl: petition.debate.public_engagement_url || '',
        debateSummaryUrl: petition.debate.debate_summary_url || '',
        overview: petition.debate.overview || ''
    } : null;

    // Process departments
    const departments = petition.departments ? petition.departments.map(dept => ({
        name: dept.name,
        acronym: dept.acronym,
        url: dept.url
    })) : [];

    return {
        id: rawData.data.id,
        title: petition.action,
        background: petition.background,
        additionalDetails: petition.additional_details,
        creatorName: petition.creator_name,
        signatureCount: petition.signature_count,
        status: petition.state,
        createdAt: petition.created_at,
        updatedAt: petition.updated_at,
        openedAt: petition.opened_at,
        // Parliament's own deadline for an open petition; closed_at stays
        // null until the day it actually closes.
        closingDate: petition.closing_date,
        closedAt: petition.closed_at,
        rejectedAt: petition.rejected_at,
        responseThresholdReached: petition.response_threshold_reached_at,
        debateThresholdReached: petition.debate_threshold_reached_at,
        moderationThresholdReached: petition.moderation_threshold_reached_at,
        governmentResponseAt: petition.government_response_at,
        debateOutcomeAt: petition.debate_outcome_at,
        scheduledDebateDate: petition.scheduled_debate_date,
        ukSignatures: ukData ? ukData.signature_count : 0,
        internationalSignatures: processedCountries,
        topConstituencies,
        regionData: sortedRegions,
        totalInternational: internationalData.reduce((sum, c) => sum + c.signature_count, 0),
        lastUpdated: new Date().toISOString(),
        signatureGrowthRate: calculateGrowthRate(petition.opened_at || petition.created_at, petition.signature_count, petition.closed_at),

        // New fields for government response and debate
        governmentResponse,
        debate,
        departments,

        // Additional metadata
        committeeNote: petition.committee_note,
        rejection: petition.rejection,
        topics: petition.topics || []
    };
};

// Calculate signature growth rate (signatures per day)
const calculateGrowthRate = (openedAt, signatureCount, closedAt = null) => {
    const start = new Date(openedAt);
    const end = closedAt ? new Date(closedAt) : new Date();
    const daysActive = Math.max(1, (end - start) / (1000 * 60 * 60 * 24));
    return Math.round(signatureCount / daysActive);
};

// Main fetch function using backend API
export const fetchPetitionData = async (url, forceRefresh = false) => {
    if (!url || !url.trim()) {
        throw new Error('Please enter a petition URL');
    }

    const petitionId = extractPetitionId(url);
    if (!petitionId) {
        throw new Error('Invalid petition URL. Please use format: https://petition.parliament.uk/petitions/{number}');
    }

    // Check frontend cache first (unless force refresh)
    if (!forceRefresh) {
        const cachedData = getCachedData(petitionId);
        if (cachedData) {
            console.log(`📦 Frontend cache hit for petition ${petitionId}`);
            return {
                ...cachedData,
                fromCache: true,
                cacheTime: cache.get(petitionId).timestamp
            };
        }
    }

    try {
        console.log(`🔍 Fetching petition ${petitionId} from backend...`);

        const response = await fetch(`${API_BASE}/petition/${petitionId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Petition #${petitionId} not found. Please check the petition ID and try again.`);
            }
            throw new Error(`Failed to fetch petition data: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        // Log cache status
        if (result.cached) {
            console.log(`📦 Backend Redis cache hit for petition ${petitionId}`);
        } else {
            console.log(`🌐 Fresh data from Parliament API for petition ${petitionId}`);
        }

        const processedData = processPetitionData(result.data);

        // Cache in frontend for short term
        setCacheData(petitionId, processedData);

        return {
            ...processedData,
            fromCache: false,
            backendCached: result.cached
        };

    } catch (error) {
        console.error('Error fetching petition data:', error);

        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            throw new Error('Unable to connect to backend server. Make sure your backend is running on http://localhost:3002');
        }

        throw new Error(error.message || 'Failed to load petition data. Please check the URL and try again.');
    }
};

// Check for data updates (compare signature counts)
export const checkForUpdates = async (currentData) => {
    if (!currentData || !currentData.id) return null;

    try {
        const url = `https://petition.parliament.uk/petitions/${currentData.id}`;
        const freshData = await fetchPetitionData(url, true); // Force refresh

        if (freshData.signatureCount !== currentData.signatureCount) {
            return {
                hasUpdate: true,
                oldCount: currentData.signatureCount,
                newCount: freshData.signatureCount,
                difference: freshData.signatureCount - currentData.signatureCount,
                data: freshData
            };
        }

        return { hasUpdate: false };
    } catch (error) {
        console.error('Error checking for updates:', error);
        return null;
    }
};

// Real-time update hook helper
export const startPolling = (petitionData, onUpdate, interval = 30000) => {
    if (!petitionData) return null;

    const pollForUpdates = async () => {
        try {
            const updateCheck = await checkForUpdates(petitionData);
            if (updateCheck && updateCheck.hasUpdate) {
                onUpdate(updateCheck);
            }
        } catch (error) {
            console.error('Polling error:', error);
        }
    };

    const intervalId = setInterval(pollForUpdates, interval);

    // Return cleanup function
    return () => clearInterval(intervalId);
};

// ===== POPULAR PETITIONS FUNCTIONS - UPDATED TO REMOVE ARTIFICIAL LIMITS =====

// Process popular petitions data for display - NO MORE ARTIFICIAL LIMITS
const processPopularPetitionsData = (rawData, limit, state) => {
    const petitions = rawData || [];

    // Sort by signature count
    const sortedPetitions = petitions
        .sort((a, b) => b.attributes.signature_count - a.attributes.signature_count);

    // Only apply limit if explicitly requested AND greater than 0
    // This maintains backward compatibility while removing artificial limits
    const finalPetitions = (limit && limit > 0) ? sortedPetitions.slice(0, limit) : sortedPetitions;

    console.log(`📊 Processing ${finalPetitions.length} ${state} petitions ${limit ? `(limited to ${limit})` : '(ALL petitions)'}`);

    // Map to a more usable format
    return finalPetitions.map(petition => ({
        id: petition.id,
        title: petition.attributes.action,
        signatureCount: petition.attributes.signature_count,
        state: petition.attributes.state,
        createdAt: petition.attributes.created_at,
        updatedAt: petition.attributes.updated_at,
        openedAt: petition.attributes.opened_at,
        closingDate: petition.attributes.closing_date,
        closedAt: petition.attributes.closed_at,
        rejectedAt: petition.attributes.rejected_at,
        creatorName: petition.attributes.creator_name,
        departmentAcronym: petition.attributes.departments?.[0]?.acronym || null,
        hasGovernmentResponse: !!petition.attributes.government_response_at,
        hasDebate: !!petition.attributes.scheduled_debate_date || !!petition.attributes.debate_outcome_at,
        url: `https://petition.parliament.uk/petitions/${petition.id}`,

        // Additional metadata for closed petitions
        responseThresholdReached: petition.attributes.response_threshold_reached_at,
        debateThresholdReached: petition.attributes.debate_threshold_reached_at,
        governmentResponseAt: petition.attributes.government_response_at,
        debateOutcomeAt: petition.attributes.debate_outcome_at,
        scheduledDebateDate: petition.attributes.scheduled_debate_date,

        // Calculate days active
        daysActive: calculateDaysActive(
            petition.attributes.opened_at || petition.attributes.created_at,
            petition.attributes.closed_at || new Date().toISOString()
        )
    }));
};

// Calculate how many days a petition has been active
const calculateDaysActive = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

// Fetch popular petitions using backend API - UPDATED TO GET ALL DATA BY DEFAULT
export const fetchPopularPetitions = async (limit = null, state = 'open') => {
    const cacheKey = `popular-petitions-${state}-${limit || 'all'}`;

    // Check frontend cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
        console.log(`📦 Frontend cache hit for ${state} petitions (${cachedData.length})`);
        return cachedData;
    }

    try {
        console.log(`🔍 Fetching ${state} petitions from backend${limit ? ` (limit: ${limit})` : ' (all)'}...`);

        // Build URL with limit parameter if provided
        const url = limit
            ? `${API_BASE}/petitions/${state}?limit=${limit}`
            : `${API_BASE}/petitions/${state}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch petitions: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        // Log cache status
        if (result.cached) {
            console.log(`📦 Backend Redis cache hit for ${state} petitions`);
        } else {
            console.log(`🌐 Fresh ${state} petitions data from Parliament API`);
        }

        // Handle the response data structure
        let petitionsData;

        // Check if data is nested or if it's already the array
        if (result.data && result.data.data) {
            petitionsData = result.data.data;
        } else if (result.data && Array.isArray(result.data)) {
            petitionsData = result.data;
        } else {
            console.error('Unexpected data structure:', result);
            petitionsData = [];
        }

        // Process the data - limit is already applied by backend if specified
        const processedData = processPopularPetitionsData(petitionsData, null, state);

        console.log(` Processed ${processedData.length} ${state} petitions${limit ? ` (requested limit: ${limit})` : ' (all available)'}`);

        // Cache in frontend for short term
        setCacheData(cacheKey, processedData);

        return processedData;

    } catch (error) {
        console.error(`Error fetching ${state} petitions:`, error);

        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            throw new Error('Unable to connect to backend server. Make sure your backend is running on http://localhost:3002');
        }

        // Return empty array on error
        return [];
    }
};

// Dedicated homepage endpoints — fast, filtered, limit 7
export const fetchHomeDebates = async () => {
    const cacheKey = 'home-debates';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${API_BASE}/home/debates`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const result = await response.json();
        const data = result.data || [];
        setCacheData(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching home debates:', error);
        return [];
    }
};

/** Petitions debated in Parliament most recently. */
export const fetchHomeDebated = async (limit = 5) => {
    const cacheKey = `home-debated-${limit}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${API_BASE}/home/debated?limit=${limit}`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const result = await response.json();
        const data = result.data || [];
        setCacheData(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching debated petitions:', error);
        return [];
    }
};

export const fetchHomePopular = async () => {
    const cacheKey = 'home-popular';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${API_BASE}/home/popular`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const result = await response.json();
        const data = result.data || [];
        setCacheData(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching home popular:', error);
        return [];
    }
};

// Headline totals for the homepage hero strip
/**
 * Whether UK Parliament is streaming right now.
 *
 * The scheduled debate date only tells us Parliament's plan; this tells us
 * whether the stream is genuinely on air, so the page can offer the video
 * only when there is something to watch.
 */
export const fetchDebateLiveStatus = async () => {
    try {
        const response = await fetch(`${API_BASE}/debate/live-status`);
        if (!response.ok) return { live: false, videoId: null };
        return await response.json();
    } catch {
        return { live: false, videoId: null };
    }
};

// StrictMode double-invokes effects in development, and any second consumer
// mounting before the first response lands would fire its own request. Sharing
// the in-flight promise collapses those into one network call.
let homeStatsInFlight = null;

export const fetchHomeStats = (() => {
    const cacheKey = 'home-stats';
    return () => {
    const cached = getCachedData(cacheKey);
    if (cached) return Promise.resolve(cached);
    if (homeStatsInFlight) return homeStatsInFlight;

    homeStatsInFlight = (async () => {
    try {
        const response = await fetch(`${API_BASE}/home/stats`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const result = await response.json();
        const data = result.data || null;
        // Don't cache a partial (cold-cache) response — the next call, once the
        // backend cache has warmed, returns the real totals.
        if (data && !data.partial) setCacheData(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching home stats:', error);
        return null;
    } finally {
        homeStatsInFlight = null;
    }
    })();

    return homeStatsInFlight;
    };
})();

// Constituencies ranked by total signatures across the current top open petitions
export const fetchPopularConstituencies = async (limit = 12) => {
    const cacheKey = `popular-constituencies-${limit}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${API_BASE}/constituencies/popular?limit=${limit}`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const result = await response.json();
        const data = result.data?.constituencies || [];
        setCacheData(cacheKey, data);
        return data;
    } catch (error) {
        console.error('Error fetching popular constituencies:', error);
        return [];
    }
};

// New helper functions for getting ALL petitions (no limits)
export const fetchAllOpenPetitions = async () => {
    return await fetchPopularPetitions(null, 'open'); // null = no limit, get ALL
};

export const fetchAllClosedPetitions = async () => {
    return await fetchPopularPetitions(null, 'closed'); // null = no limit, get ALL
};

export const fetchAllRejectedPetitions = async () => {
    return await fetchPopularPetitions(null, 'rejected'); // null = no limit, get ALL
};

export const fetchAwaitingResponsePetitions = async () => {
    return await fetchPopularPetitions(null, 'awaiting_response');
};

// A petition closes six months after opening, but its debate is frequently
// scheduled for a date after that — so the petitions most likely to have an
// upcoming debate are the closed ones. Fetching only `open` dropped them: an
// audit on 2026-09-12 found 6 scheduled debates, of which 4 were closed.
export const fetchDebateScheduledPetitions = async () => {
    const [openPetitions, closedPetitions] = await Promise.all([
        fetchPopularPetitions(null, 'open'),
        // Never let a closed-list failure empty the whole page; the open
        // petitions still render.
        fetchPopularPetitions(null, 'closed').catch(() => []),
    ]);

    const now = Date.now();
    const seen = new Set();

    return [...(openPetitions || []), ...(closedPetitions || [])]
        .filter(p => {
            if (!p || !p.scheduledDebateDate) return false;
            if (isDebatePast(p.scheduledDebateDate, now)) return false;
            // A petition can appear in both lists if it closed between fetches.
            if (seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
        })
        .sort((a, b) => getDebateTarget(a.scheduledDebateDate) - getDebateTarget(b.scheduledDebateDate));
};

// Backward compatibility functions (but with higher default limits)
export const fetchTopOpenPetitions = async (limit = 100) => {
    return await fetchPopularPetitions(limit, 'open');
};

export const fetchTopClosedPetitions = async (limit = 100) => {
    return await fetchPopularPetitions(limit, 'closed');
};

export const fetchTopRejectedPetitions = async (limit = 100) => {
    return await fetchPopularPetitions(limit, 'rejected');
};

// Clear cache functions
export const clearCache = () => {
    cache.clear();
    console.log('🗑️ Frontend cache cleared');
};

export const clearPopularPetitionsCache = () => {
    const keysToDelete = [];
    cache.forEach((value, key) => {
        if (key.startsWith('popular-petitions-')) {
            keysToDelete.push(key);
        }
    });
    keysToDelete.forEach(key => {
        console.log(`🗑️ Clearing frontend cache for: ${key}`);
        cache.delete(key);
    });
};

// Cache info functions
export const getCacheInfo = () => {
    const info = [];
    cache.forEach((value, key) => {
        info.push({
            petitionId: key,
            timestamp: value.timestamp,
            age: Date.now() - value.timestamp,
            isValid: isCacheValid(key, value.timestamp)
        });
    });
    return info;
};

export const getPopularPetitionsCacheInfo = () => {
    const info = [];
    cache.forEach((value, key) => {
        if (key.startsWith('popular-')) {
            info.push({
                key,
                timestamp: value.timestamp,
                age: Date.now() - value.timestamp,
                isValid: isCacheValid(key, value.timestamp)
            });
        }
    });
    return info;
};